/**
 * Affiliate attribution — the one thing that must survive Clerk.
 *
 * A partner link lands on the marketing site as `?via=name` (PromoteKit's
 * parameter) or `?ref=name`. Two things then try to destroy it:
 *
 *  1. The domain hop. The site is ciaotutor.com, the app is app.ciaotutor.com.
 *     A cookie on `.ciaotutor.com` does carry, but `web/src/sections.jsx` also
 *     forwards the value explicitly on the sign-up URL, so attribution doesn't
 *     depend on cookie behaviour that a browser may tighten next year.
 *  2. Clerk's post-signup redirect, which drops the query string entirely — the
 *     same reason `checkoutFlag.ts` exists. So the value is stashed on first load,
 *     before any route renders (see main.tsx).
 *
 * localStorage rather than sessionStorage: the attribution window is 60 days
 * (growth/affiliate-terms.md §2.3), not one tab. The stored stamp enforces that
 * window on read, so a ref from four months ago can't quietly claim a commission.
 *
 * Last click wins: a second partner's link overwrites the first, which is what the
 * published terms promise.
 */
const KEY = "ciaotutor_ref";
const STAMP_KEY = "ciaotutor_ref_at";
const WINDOW_MS = 60 * 24 * 60 * 60 * 1000; // 60 days
const MAX_LEN = 64;

/** Read `?via=` / `?ref=` from the current URL and persist it (no-op if absent). */
export function captureReferralFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("via") || params.get("ref");
    if (!ref) return;
    localStorage.setItem(KEY, ref.slice(0, MAX_LEN));
    localStorage.setItem(STAMP_KEY, String(Date.now()));
  } catch {
    /* localStorage unavailable — attribution is best-effort, never blocking */
  }
}

/** The stored referral, or null if absent or past the 60-day window. */
export function readReferral(): string | null {
  try {
    const ref = localStorage.getItem(KEY);
    if (!ref) return null;
    const at = Number(localStorage.getItem(STAMP_KEY) || 0);
    // A missing stamp means the value predates this file; honour it rather than
    // discarding a real partner's attribution on the deploy that introduced it.
    if (at && Date.now() - at > WINDOW_MS) {
      clearReferral();
      return null;
    }
    return ref;
  } catch {
    return null;
  }
}

/**
 * The value to attach to a Checkout Session.
 *
 * PromoteKit's script, when loaded, sets `window.promotekit_referral` to its own
 * internal referral id — that is what its dashboard matches on, so it wins over
 * our stored slug whenever it is present. Without the script (the current state:
 * no account yet, `VITE_PROMOTEKIT_ID` unset) this falls back to the captured
 * `?via=` value, which is still enough to reconcile commissions by hand from the
 * Stripe dashboard.
 */
export function checkoutReferral(): string | null {
  try {
    const fromPromoteKit = (window as unknown as { promotekit_referral?: string })
      .promotekit_referral;
    if (fromPromoteKit) return String(fromPromoteKit).slice(0, MAX_LEN);
  } catch {
    /* fall through to the stored value */
  }
  return readReferral();
}

export function clearReferral(): void {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(STAMP_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Load PromoteKit's tracker, if configured.
 *
 * Inert until `VITE_PROMOTEKIT_ID` is set at build time, so this ships before the
 * account exists and goes live with one environment variable and a rebuild — no
 * code change at the point where money is involved.
 */
export function initPromoteKit(): void {
  const id = import.meta.env.VITE_PROMOTEKIT_ID as string | undefined;
  if (!id || typeof document === "undefined") return;
  if (document.querySelector("script[data-promotekit]")) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://cdn.promotekit.com/promotekit.js";
  s.dataset.promotekit = id;
  document.head.appendChild(s);
}
